//@flow
import * as React from 'react';
import * as Immutable from 'immutable';
import compose from 'recompose/compose';
import { themr } from 'react-css-themr';
import defaultTheme from './PjpStepsContainer.scss';
import {
    withRouter,
    navigationPromptFactory
} from 'common/components/core/router';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import {
    teamIdQuerySelector,
    homebaseIdQuerySelector
} from 'common/redux/selectors/queryParamsSelectors';
import { clientIdSelector } from 'common/redux/selectors/sessionSelector';
import {
    getHomebasesIfPresent,
    isFetchHomebaseInProgress
} from 'common/redux/selectors/homebase';
import { fetchHomebasesByClientId } from 'common/redux/actions/homebase';
import { personnelActiveTeamValuesSelector } from 'common/redux/selectors/personnelTeamsSelector';
import { Flex } from '@locus-taxy/neo-ui';

import SchedulingConfigSelectors from 'common/redux/selectors/schedulingConfig';
import { getDefaultSalesCategoriesCapacitiesList } from 'common/redux/selectors/salesCategories';
import { withStatechart, State } from 'react-automata';
import {
    CreatePjpStateChart,
    PjpStepsList
} from 'common/entity/pjpMaster/create/CreatePjpStateChart';
import SalesCategoriesActions from 'common/redux/actions/salesCategories';
import CreatePjpRiderMasterContainer from 'common/entity/pjpMaster/create/CreatePjpRiderMasterContainer';
import CreatePjpOutletMasterContainer from 'common/entity/pjpMaster/create/CreatePjpOutletMasterContainer';
import PjpConfigContainer from 'common/entity/pjpMaster/create/PjpConfigContainer';
import { headerWithLogoutAndBUFactory } from 'common/components/core/app_bar/HeaderWithLogoutAndBU';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import PjpActions from 'common/redux/actions/pjpMaster';
import Steps from 'common/components/core/steps';
import TeamChip from 'common/components/core/common/TeamChip';
import HomebaseChip from 'common/components/core/common/HomebaseChip';
import { Loader } from 'common/components/core/loader/Loader';
import { VehicleSelectors } from 'common/redux/selectors/vehicle';

import { CreatePjpRequest } from 'common/models/internal/CreatePjpRequest';
import {
    PjPlanConfiguration,
    TransportModeEnum
} from 'common/models/internal/PjPlanConfiguration';

import AlertDialog from 'common/components/core/dialog/AlertDialog';
import { matchesState } from 'xstate';
import { SizeEnum } from 'common/enums/UIEnums';
import { ApiError } from 'common/modules/api';
import { Homebase } from 'common/models/internal/Homebase';
import type { List } from 'immutable';
import { PermanentJourneyPlan } from 'common/models/internal/PermanentJourneyPlan';
import { IncrementalPjpContext } from 'common/entity/pjpMaster/incremental/IncrementalPjpContext';

const NavigationPrompt = navigationPromptFactory({
    Prompt: AlertDialog
});
const DEFAULT_ACTIVE_FILTER = [
    new FilterBuilder()
        .withName('status')
        .withOperation(FilterOperation.Equals)
        .withValue('ACTIVE')
];

type Props = {
    theme: Object,
    homebaseId?: string,
    teamId?: string,
    firstSchedulingConfigId?: string,
    clientId: string,
    defaultSalesCategoryCapacitiesList: List<any>,
    homebases: List<Homebase>,
    isFetchingHomebases: boolean,
    onDismiss: () => void,
    addPjp: () => void,
    fetchOutletSalesCategories: () => void,
    fetchSalesCategories: () => void,
    onCreate: ({
        clientId: string,
        homebaseId: ?string,
        teamId: ?string
    }) => void,
    onCancel: () => void,
    updatePjpLimitStatus: Function,
    selectedPlan?: PermanentJourneyPlan
};

type StateType = {
    errorMessage: ?string,
    pjpCreateInProgress: boolean,
    navigationPromptDisabled: boolean,
    pjpCreationRequest: CreatePjpRequest,
    isIncrementalPlan: boolean
};
const PjpSteps = ({ currentStep, steps, theme }) => {
    return (
        <Steps
            size={SizeEnum.Large}
            currentStep={currentStep}
            steps={steps}
            theme={theme}
            className={theme.pjpHeaderSteps}
        />
    );
};
const TeamAndHomebaseChip = ({
    teams,
    teamId,
    homebases,
    homebaseId,
    theme
}) => {
    const selectedTeam =
        teams && teams.find(homebase => homebase.teamId === teamId);
    const selectedHomebase =
        homebases && homebases.find(homebase => homebase.id === homebaseId);
    const homebaseName =
        selectedHomebase &&
        (selectedHomebase.getPlaceName() ||
            selectedHomebase.getAddressString());
    return (
        <Flex className={theme.teamHomebaseWrap}>
            <TeamChip team={selectedTeam} theme={theme} />
            <HomebaseChip theme={theme} homebaseName={homebaseName} invert />
        </Flex>
    );
};
const HeaderWithLogoutAndBU = headerWithLogoutAndBUFactory({
    Left: PjpSteps,
    Right: TeamAndHomebaseChip
});

const CreatePjpStepsHeader = ({
    steps,
    currentStep,
    theme,
    teams,
    teamId,
    homebases,
    homebaseId
}) => {
    return (
        <HeaderWithLogoutAndBU
            hideBUSelector
            steps={steps}
            currentStep={currentStep}
            theme={theme}
            teams={teams}
            teamId={teamId}
            homebases={homebases}
            homebaseId={homebaseId}
        />
    );
};

const PjpStepsFactory = ({ OutletContainer, RiderContainer }) => {
    class PjpStepsContainer extends React.PureComponent<Props, StateType> {
        constructor(props) {
            super(props);

            const defaultConfiguration = {
                schedulingConfigId: {
                    clientId: props.clientId,
                    configId: props.firstSchedulingConfigId
                },
                needsRevenueFairnessAcrossDays: false,
                autoAssign: true,
                needsExistingBeatPlan: false,
                transportMode: TransportModeEnum.Walking,
                distributionPlanConfiguration: {
                    distributionOffset: props.defaultDistributionOffset,
                    needsDistributionBeatPlan: false
                }
            };

            this.state = {
                pjpCreateInProgress: false,
                errorMessage: null,
                navigationPromptDisabled: false,
                allFilteredOutlets: { count: 0, areAllSelected: false },
                allFilteredRiders: { count: 0, areAllSelected: false },
                defaultSerializedConfig: null,
                pjpCreationRequest: CreatePjpRequest.fromJS({
                    clientId: this.props.clientId,
                    homebaseId: this.props.homebaseId,
                    teamId: this.props.teamId,
                    outletsData: { ids: [], request: {} },
                    ridersData: { ids: [], request: {} },
                    configuration:
                        props.selectedPlan?.configuration ||
                        defaultConfiguration
                }),
                isIncrementalPlan: !!props.selectedPlan
            };
        }

        componentDidMount() {
            const { homebases, isFetchingHomebases } = this.props;
            if (!homebases && !isFetchingHomebases) {
                this.fetchHomebases();
            }
        }

        componentDidUpdate(prevProps: Props) {
            const {
                teamId: prevTeamId,
                homebaseId: prevHomebaseId,
                firstSchedulingConfigId: prevFirstSchedulingConfigId,
                defaultSalesCategoryCapacitiesList: prevDefaultSalesCategoryCapacitiesList
            } = prevProps;
            const {
                teamId,
                homebaseId,
                firstSchedulingConfigId,
                defaultSalesCategoryCapacitiesList
            } = this.props;
            let { pjpCreationRequest } = this.state;
            if (teamId && teamId !== prevTeamId) {
                pjpCreationRequest = pjpCreationRequest.setTeamId(teamId);
            }
            if (homebaseId && homebaseId !== prevHomebaseId) {
                pjpCreationRequest = pjpCreationRequest.setHomebaseId(
                    homebaseId
                );
            }
            if (
                defaultSalesCategoryCapacitiesList &&
                defaultSalesCategoryCapacitiesList !==
                    prevDefaultSalesCategoryCapacitiesList
            ) {
                let {
                    configuration: updatedConfiguration
                } = pjpCreationRequest;
                updatedConfiguration = updatedConfiguration.setCapacitiesList(
                    defaultSalesCategoryCapacitiesList
                );
                pjpCreationRequest = pjpCreationRequest.setConfiguration(
                    updatedConfiguration
                );
            }

            if (
                firstSchedulingConfigId !== prevFirstSchedulingConfigId &&
                !this.state.isIncrementalPlan
            ) {
                let {
                    configuration: updatedConfiguration
                } = pjpCreationRequest;
                const updatedSchedulingConfigId = updatedConfiguration.schedulingConfigId.setConfigId(
                    firstSchedulingConfigId
                );
                updatedConfiguration = updatedConfiguration.setSchedulingConfigId(
                    updatedSchedulingConfigId
                );
                pjpCreationRequest = pjpCreationRequest.setConfiguration(
                    updatedConfiguration
                );
            }

            this.setState({ pjpCreationRequest });
        }

        static contextType = IncrementalPjpContext;

        setDefaultSerializedConfig = config => {
            this.setState({
                defaultSerializedConfig: config
            });
        };

        fetchHomebases = () => {
            const { clientId, fetchHomebasesByClientId } = this.props;
            fetchHomebasesByClientId(clientId);
        };

        onConfigurationChange = (pjpConfiguration: PjPlanConfiguration) => {
            this.setState(prevState => {
                return {
                    pjpCreationRequest: prevState.pjpCreationRequest.setConfiguration(
                        pjpConfiguration
                    )
                };
            });
        };

        onRidersDataChange = (
            {
                selectedRows,
                allApplicableFilters,
                allFilteredEntitiesSelected,
                filteredEntriesCount
            },
            transitionOnly = false
        ) => {
            const { transition } = this.props;
            if (!transitionOnly) {
                this.setState(prevState => {
                    const { pjpCreationRequest } = prevState;
                    let { ridersData } = pjpCreationRequest;
                    let allRidersSelected = false;
                    if (allFilteredEntitiesSelected) {
                        allRidersSelected = true;
                        ridersData = ridersData.setRequest(
                            ridersData.request.setFilters(
                                Immutable.List(
                                    allApplicableFilters.addAll(
                                        DEFAULT_ACTIVE_FILTER
                                    ).list
                                )
                            )
                        );
                    } else {
                        ridersData = ridersData.setIds(
                            Immutable.List(selectedRows)
                        );
                    }
                    return {
                        pjpCreationRequest: pjpCreationRequest.setRidersData(
                            ridersData
                        ),
                        allFilteredRiders: {
                            areAllSelected: allRidersSelected,
                            count: filteredEntriesCount
                        }
                    };
                });
            }
            transition('NEXT');
        };

        onOutletsDataChange = (
            {
                selectedRows,
                allApplicableFilters,
                allFilteredEntitiesSelected,
                filteredEntriesCount
            },
            transitionOnly = false
        ) => {
            const {
                transition,
                fetchOutletSalesCategories,
                clientId
            } = this.props;
            if (!transitionOnly) {
                let allOutletsSelected = false;
                this.setState(prevState => {
                    const { pjpCreationRequest } = prevState;
                    let { outletsData } = pjpCreationRequest;
                    if (allFilteredEntitiesSelected) {
                        allOutletsSelected = true;
                        outletsData = outletsData.setRequest(
                            outletsData.request.setFilters(
                                Immutable.List(
                                    allApplicableFilters.addAll(
                                        DEFAULT_ACTIVE_FILTER
                                    ).list
                                )
                            )
                        );
                    } else {
                        outletsData = outletsData.setIds(
                            Immutable.List(selectedRows)
                        );
                    }
                    const { ids, request } = outletsData;
                    fetchOutletSalesCategories(
                        { clientId },
                        {
                            ids,
                            request: request?.toJS()
                        }
                    );
                    return {
                        pjpCreationRequest: pjpCreationRequest.setOutletsData(
                            outletsData
                        ),
                        allFilteredOutlets: {
                            areAllSelected: allOutletsSelected,
                            count: filteredEntriesCount
                        }
                    };
                });
            }
            transition('NEXT');
        };

        disableNavigationPrompt = () => {
            this.setState({
                navigationPromptDisabled: true
            });
        };
        isSchedulingConfigUpdated = () => {
            const { pjpCreationRequest, defaultSerializedConfig } = this.state;
            return (
                pjpCreationRequest.configuration.serializedSchedulingConfig ===
                defaultSerializedConfig
            );
        };

        clearErrorMessage = () => {
            // Clear message after 5 sec
            setTimeout(() => {
                this.setState({
                    errorMessage: null
                });
            }, 5000);
        };

        validateForm = () => {
            const { pjpCreationRequest } = this.state;
            const updatedPjpCreationRequest = pjpCreationRequest.setAllValidationErrors();

            this.setState({ pjpCreationRequest: updatedPjpCreationRequest });
            return !pjpCreationRequest.hasValidationErrors();
        };

        onCreatePjpStart = () => {
            const { onDismiss, updatePjpLimitStatus } = this.props;
            if (this.context?.onPlanCreate) {
                this.setState({ pjpCreateInProgress: true });
                this.context.onPlanCreate({
                    onSuccess: () => {
                        this.disableNavigationPrompt();
                        this.setState({ pjpCreateInProgress: false });
                        onDismiss(true);
                        if (updatePjpLimitStatus) {
                            updatePjpLimitStatus();
                        }
                    },
                    onFailure: error => {
                        this.setState(
                            {
                                pjpCreateInProgress: false,
                                errorMessage: error.message
                            },
                            this.clearErrorMessage
                        );
                    }
                });
                return;
            }
            const { pjpCreationRequest } = this.state;
            const { addPjp, clientId, t } = this.props;

            if (this.validateForm()) {
                this.setState({ pjpCreateInProgress: true });
                const pjpConfiguration = pjpCreationRequest.configuration;
                let updatedPjpCreationRequest;
                updatedPjpCreationRequest = pjpCreationRequest.setConfiguration(
                    pjpConfiguration.sanitizeDummyPersona()
                );
                updatedPjpCreationRequest = this.isSchedulingConfigUpdated()
                    ? updatedPjpCreationRequest.setConfiguration(
                          updatedPjpCreationRequest.configuration.setSerializedSchedulingConfig(
                              null
                          )
                      )
                    : pjpCreationRequest;
                addPjp({ clientId }, updatedPjpCreationRequest)
                    .then(() => {
                        this.disableNavigationPrompt();
                        this.setState({ pjpCreateInProgress: false });
                        onDismiss(true);
                        if (updatePjpLimitStatus) {
                            updatePjpLimitStatus();
                        }
                    })
                    .catch(error => {
                        if (error instanceof ApiError) {
                            this.setState(
                                {
                                    pjpCreateInProgress: false,
                                    errorMessage: error.message
                                },
                                this.clearErrorMessage
                            );
                            return;
                        } else {
                            throw error;
                        }
                    });
            } else {
                this.setState(
                    {
                        errorMessage: t(
                            'validations:pjpCreationValidationFailMessage'
                        )
                    },
                    this.clearErrorMessage
                );
            }
        };

        getPjpStepsList = () => {
            const {
                pjpCreationRequest,
                allFilteredOutlets,
                allFilteredRiders
            } = this.state;
            return PjpStepsList.map(step => {
                const outletIdsSize =
                    allFilteredOutlets.areAllSelected &&
                    allFilteredOutlets.count
                        ? allFilteredOutlets.count
                        : pjpCreationRequest.outletsData.ids.size;
                const riderIdsSize =
                    allFilteredRiders.areAllSelected && allFilteredRiders.count
                        ? allFilteredRiders.count
                        : pjpCreationRequest.ridersData.ids.size;
                switch (step.id) {
                    case 'outletSelection':
                        return {
                            ...step,
                            subtitle: outletIdsSize
                        };
                    case 'riderSelection':
                        return {
                            ...step,
                            subtitle: riderIdsSize
                        };
                    default:
                        return step;
                }
            });
        };

        getTeamFilters = () => {
            const { teamId, clientId } = this.props;
            return [
                new FilterBuilder()
                    .withName('teams')
                    .withOperation(FilterOperation.Contains)
                    .withValue({
                        clientId,
                        teamId
                    })
                    .withCaseSensitive(true)
            ];
        };

        getSchedulingConfigFilters = () => {
            const { teamId, clientId } = this.props;
            return [
                ...DEFAULT_ACTIVE_FILTER,
                new FilterBuilder()
                    .withName('teams')
                    .withOperation(FilterOperation.Contains)
                    .withValue({
                        clientId,
                        teamId
                    })
                    .withAllowEmptyOrNull(true)
            ];
        };

        onPreviousClick = () => {
            const { transition } = this.props;
            transition('PREV');
        };

        render() {
            const {
                theme,
                t,
                machineState,
                teamId,
                homebaseId,
                teams,
                homebases,
                location,
                onDismiss,
                match,
                history
            } = this.props;
            const {
                pjpCreationRequest,
                navigationPromptDisabled,
                pjpCreateInProgress,
                errorMessage,
                isIncrementalPlan
            } = this.state;
            const { pathName, search } = location;
            const whiteListedPathnames = [`${pathName}${search}`];
            return (
                <div className={theme.pjpStepsContainer}>
                    <CreatePjpStepsHeader
                        steps={this.getPjpStepsList()}
                        theme={theme}
                        currentStep={machineState.value}
                        teamId={teamId}
                        homebaseId={homebaseId}
                        teams={teams}
                        homebases={homebases}
                    />
                    {pjpCreateInProgress ? (
                        <Loader active theme={theme} />
                    ) : null}
                    <NavigationPrompt
                        when={
                            !navigationPromptDisabled &&
                            (matchesState(machineState, 'riderSelection') ||
                                matchesState(
                                    machineState,
                                    'configurationSelection'
                                ))
                        }
                        cancelLabel={t('translations:stay')}
                        confirmLabel={t('translations:leave')}
                        whiteListedPathnames={whiteListedPathnames}
                        title={t('translations:leaveThisPage')}
                        searchQueryCheck
                        cancelButtonTestId="confirm"
                        stayButtonTestId="cancel"
                    >
                        <span>
                            {t('translations:unsavedChangesWillBeLost')}
                        </span>
                    </NavigationPrompt>
                    <State value="outletSelection">
                        <OutletContainer
                            title={t('pjpCreate.selectOutlets')}
                            theme={theme}
                            customActions={{
                                onNext: this.onOutletsDataChange,
                                onCancel: onDismiss
                            }}
                            enableFilteredEntitiesSelection
                            errorMessage={errorMessage}
                            getExtraFiltersForFetchData={this.getTeamFilters}
                            match={match}
                            location={location}
                            history={history}
                        />
                    </State>
                    <State value="riderSelection">
                        <RiderContainer
                            title={t('pjpCreate.selectRiders')}
                            theme={theme}
                            customActions={{
                                onNext: this.onRidersDataChange,
                                onPrev: this.onPreviousClick,
                                onCancel: onDismiss
                            }}
                            enableFilteredEntitiesSelection
                            errorMessage={errorMessage}
                            getExtraFiltersForFetchData={this.getTeamFilters}
                            match={match}
                            location={location}
                            history={history}
                        />
                    </State>
                    <State value="configurationSelection">
                        <PjpConfigContainer
                            t={t}
                            setDefaultSerializedConfig={
                                this.setDefaultSerializedConfig
                            }
                            isIncrementalPlan={isIncrementalPlan}
                            theme={theme}
                            onNext={this.onCreatePjpStart}
                            onCancel={onDismiss}
                            onPrev={this.onPreviousClick}
                            pjpConfiguration={pjpCreationRequest.configuration}
                            onConfigurationChange={this.onConfigurationChange}
                            errorMessage={errorMessage}
                            outletsData={pjpCreationRequest.outletsData}
                            schedulingConfigFilters={
                                this.getSchedulingConfigFilters
                            }
                        />
                    </State>
                </div>
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const teamId = teamIdQuerySelector(state, ownProps);
        const homebaseId = homebaseIdQuerySelector(state, ownProps);
        const clientId = clientIdSelector(state, ownProps);
        const firstSchedulingConfigId = SchedulingConfigSelectors.getFirstSchedulingConfigId(
            state,
            ownProps
        );
        const defaultSalesCategoryCapacitiesList = getDefaultSalesCategoriesCapacitiesList(
            state,
            ownProps
        );
        const teams = personnelActiveTeamValuesSelector(state, ownProps);
        const homebases = getHomebasesIfPresent(state, ownProps);
        const isFetchingHomebases = isFetchHomebaseInProgress(state, ownProps);
        const defaultDistributionOffset = VehicleSelectors.getDefaultDistributionOffset(
            state,
            ownProps
        );
        return {
            teamId,
            homebaseId,
            clientId,
            firstSchedulingConfigId,
            defaultSalesCategoryCapacitiesList,
            teams,
            homebases,
            isFetchingHomebases,
            defaultDistributionOffset
        };
    };

    const withRedux = connect(mapStateToProps, {
        addPjp: PjpActions.addEntities,
        fetchSalesCategories: SalesCategoriesActions.fetchSalesCategories,
        fetchOutletSalesCategories:
            SalesCategoriesActions.fetchOutletSalesCategories,
        fetchHomebasesByClientId
    });

    const ConnectedPjpStepsContainer = compose(
        withRouter,
        withRedux,
        withStatechart(CreatePjpStateChart),
        themr('PjpStepsContainer', defaultTheme),
        translate('entityManagement')
    )(PjpStepsContainer);

    return ConnectedPjpStepsContainer;
};

const ConnectedPjpStepsContainer = PjpStepsFactory({
    OutletContainer: CreatePjpOutletMasterContainer,
    RiderContainer: CreatePjpRiderMasterContainer
});

export default ConnectedPjpStepsContainer;

export { PjpStepsFactory, ConnectedPjpStepsContainer as PjpStepsContainer };
